import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedSection } from "./animations";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { useNonFeaturedCaseStudies, useFeaturedCaseStudies } from "@/hooks/useCaseStudies";
import { Skeleton } from "@/components/ui/skeleton";
import { isVideoUrl } from "@/utils/mediaHelpers";

import type { Easing } from "framer-motion";

// Shared animation config
const easeStandard: Easing = [0.25, 0.4, 0.25, 1];

// One-line framing per engagement, used under the card title
const SUMMARIES: Record<string, string> = {
  spai: "A spatial interface for AI conversations with branching, inspectable context, scoped memory and generative working surfaces.",
  luno: "A decision-intelligence layer that gives leadership teams a system of record for reasoning, not just status.",
  rekhta: "Digital infrastructure for a language: a trilingual knowledge system serving 300K+ entries across three scripts.",
  leonardo: "Agent workflows that turned manual review passes into a repeatable creative and technical standard.",
  "future-fabric": "Fine-tuned generation with composition locked, turning multi-week visual passes into same-day iteration.",
  
};

const CaseStudyList = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { data: nonFeaturedCaseStudies, isLoading } = useNonFeaturedCaseStudies();
  const { data: featuredProjects, isLoading: featuredLoading } = useFeaturedCaseStudies();

  if (isLoading || featuredLoading) {
    return (
      <section id="work">
        {/* Featured Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2">
          {[0, 1, 2, 3].map((index) =>
          <div
            key={index}
            className={`aspect-[4/3] border-b border-border ${
            index % 2 === 0 ? "md:border-r" : ""}`
            }>
            
              <Skeleton className="w-full h-full" />
            </div>
          )}
        </div>
        {/* List Skeleton */}
        <div className="border-b border-border px-6 md:px-12 py-4">
          <Skeleton className="h-3 w-16" />
        </div>
        {[0, 1, 2].map((index) =>
        <div
          key={index}
          className="flex items-center justify-between px-6 md:px-12 py-5 border-b border-border">
          
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-24" />
          </div>
        )}
      </section>);

  }

  const otherCaseStudies = (nonFeaturedCaseStudies || []).filter(
    (cs) => cs.slug !== "spai" && cs.slug !== "one8" && cs.slug !== "future-fabric" && cs.slug !== "itc-store"
  );
  const featured = (featuredProjects || []).filter(
    (project) => project.slug !== "kaan-air" && project.slug !== "one8"
  );
  const hoveredProject = hoveredIndex !== null ? otherCaseStudies[hoveredIndex] : null;
  const hoveredMediaUrl = hoveredProject?.thumbnail_url || hoveredProject?.hero_image_url || null;

  return (
    <section id="work" className="pb-6 md:pb-10">
      {/* Featured Image Grid - 2x2 */}
      <div className="grid grid-cols-1 md:grid-cols-2">
        {featured.filter((p) => p.slug !== "rekhta" && p.slug !== "leonardo").slice(0, 3).map((project, index) =>
        <motion.div
          key={project.slug}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{
            duration: 0.6,
            delay: index * 0.15,
            ease: easeStandard
          }}>
          
            <Link
            to={`/work/${project.slug}`}
            className={`group relative aspect-[4/3] overflow-hidden border-b border-border flex ${
            index % 2 === 0 ? "md:border-r" : ""}`
            }>
            
              <motion.div
              className="absolute inset-0"
              whileHover={{ scale: 1.08 }}
              transition={{ duration: 0.6, ease: easeStandard }}>
              
                {(() => {
                const mediaUrl = project.thumbnail_url || project.hero_image_url;
                if (!mediaUrl) {
                  return (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="text-muted-foreground font-display text-4xl">
                          {project.title.charAt(0)}
                        </span>
                      </div>);

                }
                if (isVideoUrl(mediaUrl)) {
                  return (
                    <video
                      src={mediaUrl}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-full object-cover" />);


                }
                return (
                  <img
                    src={mediaUrl}
                    alt={project.title}
                    className="w-full h-full object-cover"
                    loading="lazy" />);


              })()}
              </motion.div>

              <motion.div
              className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"
              initial={{ opacity: 0.7 }}
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.35 }} />
            

              <motion.div
              className="absolute top-4 left-4 text-white font-display text-lg z-10 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-lg"
              whileHover={{ scale: 1.08, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}>
              
                {project.client_name || project.title.charAt(0)}
              </motion.div>

              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 overflow-hidden z-10">
                <motion.div
                className="flex items-end justify-between"
                initial={{ y: 10, opacity: 0.9 }}
                whileHover={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.35 }}>
                
                  <div>
                    <span className="text-white/60 text-sm uppercase tracking-wider block mb-2">
                      {project.role || "Design"}
                    </span>
                    <h3 className="font-display text-3xl md:text-5xl uppercase text-white tracking-tight">
                      {project.title}
                    </h3>
                    {SUMMARIES[project.slug] && (
                      <p className="hidden sm:block mt-3 text-[13px] text-white/70 leading-relaxed max-w-md">
                        {SUMMARIES[project.slug]}
                      </p>
                    )}
                  </div>
                  <motion.div
                  className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-medium"
                  whileHover={{ scale: 1.08 }}>
                  
                    <ArrowRight className="w-4 h-4 text-white" />
                  </motion.div>
                </motion.div>

                <motion.div
                className="h-0.5 bg-accent mt-4 rounded-full"
                initial={{ scaleX: 0, originX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.4 }} />
              
              </div>
            </Link>
          </motion.div>
        )}

        {/* Hardcoded Rekhta Dictionary featured card */}
        <motion.div
          key="rekhta-featured"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{
            duration: 0.6,
            delay: featured.filter((p) => p.slug !== "rekhta").slice(0, 3).length * 0.15,
            ease: easeStandard
          }}>
          
          <Link
            to="/work/rekhta"
            className={`group relative aspect-[4/3] overflow-hidden border-b border-border flex ${
            featured.filter((p) => p.slug !== "rekhta").slice(0, 3).length % 2 === 0 ? "md:border-r" : ""}`
            }>
            
            <motion.div
              className="absolute inset-0"
              whileHover={{ scale: 1.08 }}
              transition={{ duration: 0.6, ease: easeStandard }}>

              {/* Trilingual word card, static composition reflecting the dictionary's signature surface */}
              <div
                className="w-full h-full relative overflow-hidden"
                style={{ background: "radial-gradient(ellipse at 30% 20%, #16306b 0%, #0B1F4A 55%, #060f2a 100%)" }}>

                {/* Subtle grid */}
                <div
                  className="absolute inset-0 opacity-[0.07]"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
                    backgroundSize: "32px 32px"
                  }} />

                {/* Oversized Nastaliq glyph as background art */}
                <span
                  dir="rtl"
                  className="absolute font-serif select-none pointer-events-none"
                  style={{
                    fontSize: "clamp(280px, 55%, 520px)",
                    lineHeight: 0.8,
                    color: "#ffffff",
                    opacity: 0.07,
                    right: "-4%",
                    top: "-12%",
                    fontFamily: "'Noto Naskh Arabic', 'Amiri', serif"
                  }}>
                  عشق
                </span>

                {/* Top meta strip */}
                <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-2">
                    <span className="font-serif italic text-base" style={{ color: "#ff8a80" }}>rekhta</span>
                    <span className="text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm text-white" style={{ background: "#1565C0" }}>
                      Dictionary
                    </span>
                  </div>
                  <span className="font-mono text-[9px] tracking-widest uppercase text-white/40">
                    01 / Trilingual
                  </span>
                </div>

                {/* Center word card */}
                <div className="absolute inset-0 flex items-center justify-center px-6">
                  <div className="w-full max-w-[78%] rounded-xl border border-white/10 backdrop-blur-sm px-5 py-4 md:px-6 md:py-5"
                    style={{ background: "rgba(255,255,255,0.04)" }}>

                    {/* Roman headword */}
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-display uppercase tracking-tight text-white text-3xl md:text-4xl leading-none">
                        'ishq
                      </span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                        n. · Arabic
                      </span>
                    </div>

                    {/* Hairline */}
                    <div className="my-3 h-px w-full" style={{ background: "linear-gradient(to right, transparent, rgba(255,255,255,0.18), transparent)" }} />

                    {/* Trilingual row */}
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-white/85 text-lg md:text-xl" style={{ fontFamily: "'Noto Sans Devanagari', serif" }}>
                        इश्क़
                      </span>
                      <span className="w-1 h-1 rounded-full bg-white/30" />
                      <span dir="rtl" className="text-white text-2xl md:text-3xl" style={{ fontFamily: "'Noto Naskh Arabic', serif" }}>
                        عشق
                      </span>
                    </div>

                    {/* Meaning */}
                    <p className="mt-3 text-[11px] md:text-xs leading-relaxed text-white/55">
                      strong emotional attachment, all-consuming love
                    </p>
                  </div>
                </div>

                {/* Bottom corner accent */}
                <div className="absolute bottom-4 left-5 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#1565C0", boxShadow: "0 0 12px #1565C0" }} />
                  <span className="font-mono text-[9px] tracking-widest uppercase text-white/40">
                    Knowledge Graph · 3 Scripts
                  </span>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"
              initial={{ opacity: 0.7 }}
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.35 }} />
            

            <motion.div
              className="absolute top-4 left-4 text-white font-display text-lg z-10 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-lg"
              whileHover={{ scale: 1.08, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}>
              
              Rekhta
            </motion.div>

            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 overflow-hidden z-10">
              <motion.div
                className="flex items-end justify-between"
                initial={{ y: 10, opacity: 0.9 }}
                whileHover={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.35 }}>
                
                <div>
                  <span className="text-white/60 text-sm uppercase tracking-wider block mb-2">
                    Product Strategy · UX/UI
                  </span>
                  <h3 className="font-display text-3xl md:text-5xl uppercase text-white tracking-tight">
                    Rekhta Dictionary
                  </h3>
                  <p className="hidden sm:block mt-3 text-[13px] text-white/70 leading-relaxed max-w-md">
                    {SUMMARIES["rekhta"]}
                  </p>
                </div>
                <motion.div
                  className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-medium"
                  whileHover={{ scale: 1.08 }}>
                  
                  <ArrowRight className="w-4 h-4 text-white" />
                </motion.div>
              </motion.div>

              <motion.div
                className="h-0.5 mt-4 rounded-full"
                style={{ backgroundColor: "#1565C0" }}
                initial={{ scaleX: 0, originX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.4 }} />
              
            </div>
          </Link>
        </motion.div>
      </div>

      {/* Other, slim section label */}
      <div className="border-b border-border">
        <div className="px-6 md:px-12 py-4">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Other</span>
        </div>
      </div>





      {/* Project List */}
      <div className="relative">
        {/* Floating thumbnail preview */}
        <AnimatePresence>
          {hoveredIndex !== null && hoveredMediaUrl &&
          <motion.div
            className="fixed pointer-events-none z-50 w-64 h-40 rounded-xl overflow-hidden shadow-2xl"
            style={{
              top: "50%",
              right: "10%",
              translateY: "-50%"
            }}
            initial={{ opacity: 0, scale: 0.8, x: 50 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.8, x: 50 }}
            transition={{ duration: 0.35, ease: easeStandard }}>
            
              {isVideoUrl(hoveredMediaUrl) ?
            <video
              src={hoveredMediaUrl}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover" /> :


            <img
              src={hoveredMediaUrl}
              alt={hoveredProject?.title || "Project thumbnail"}
              className="w-full h-full object-cover"
              loading="lazy" />

            }
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </motion.div>
          }
        </AnimatePresence>

        {/* Hardcoded Kaan Air entry */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, ease: easeStandard }}>
          <Link
            to="/work/spai"
            className="group flex min-h-[44px] items-center justify-between overflow-hidden border-b border-border px-6 py-3 md:px-12 md:py-4 relative">
            <div className="absolute inset-0 translate-x-[-100%] bg-foreground transition-transform duration-slow ease-out group-hover:translate-x-0" />
            <div className="absolute bottom-0 left-0 top-0 w-1 origin-top scale-y-0 bg-accent transition-transform duration-medium group-hover:scale-y-100" />
            <div className="relative z-10 flex items-baseline gap-4 transition-transform duration-medium group-hover:translate-x-4">
              <span className="font-display text-2xl uppercase tracking-tight transition-colors duration-medium group-hover:text-background md:text-3xl">SPai</span>
              <span className="font-mono text-sm text-muted-foreground transition-colors duration-medium group-hover:text-background/60">2026</span>
            </div>
            <div className="relative z-10 flex items-center gap-6">
              <span className="hidden text-sm uppercase tracking-wider text-muted-foreground transition-colors duration-medium group-hover:text-background/60 sm:block">Spatial interface for thinking with AI</span>
              <div className="flex h-8 w-8 translate-x-2 items-center justify-center rounded-full bg-muted opacity-0 transition-all duration-medium group-hover:translate-x-0 group-hover:bg-background/20 group-hover:opacity-100">
                <ArrowRight className="h-4 w-4 transition-colors duration-medium group-hover:text-background" />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Hardcoded Leonardo entry */}


        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, ease: easeStandard }}>

          <Link
            to="/work/leonardo"
            className="group flex items-center justify-between px-6 md:px-12 py-3 md:py-4 border-b border-border relative overflow-hidden min-h-[44px]">

            <div className="absolute inset-0 bg-foreground translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-slow ease-out" />
            <div
              className="absolute left-0 top-0 bottom-0 w-1 scale-y-0 group-hover:scale-y-100 transition-transform duration-medium origin-top"
              style={{ backgroundColor: "#6aa6ff" }} />

            <div className="flex items-baseline gap-4 relative z-10 group-hover:translate-x-4 transition-transform duration-medium">
              <span className="font-display text-2xl md:text-3xl uppercase tracking-tight group-hover:text-background transition-colors duration-medium">
                Leonardo Helicopters
              </span>
              <span className="font-mono text-sm text-muted-foreground group-hover:text-background/60 transition-colors duration-medium">
                2025
              </span>
            </div>
            <div className="flex items-center gap-6 relative z-10">
              <span className="text-sm uppercase tracking-wider text-muted-foreground group-hover:text-background/60 transition-colors duration-medium hidden sm:block">
                Agent workflows for creative review
              </span>
              <div className="w-8 h-8 rounded-full bg-muted group-hover:bg-background/20 flex items-center justify-center transition-all duration-medium group-hover:translate-x-0 translate-x-2 opacity-0 group-hover:opacity-100">
                <ArrowRight className="w-4 h-4 group-hover:text-background transition-colors duration-medium" />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Hardcoded Future Fabric (StrategY&) entry */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, delay: 0.2, ease: easeStandard }}>

          <Link
            to="/work/future-fabric"
            className="group flex items-center justify-between px-6 md:px-12 py-3 md:py-4 border-b border-border relative overflow-hidden min-h-[44px]">

            <div className="absolute inset-0 bg-foreground translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-slow ease-out" />
            <div
              className="absolute left-0 top-0 bottom-0 w-1 scale-y-0 group-hover:scale-y-100 transition-transform duration-medium origin-top"
              style={{ backgroundColor: "#D4A853" }} />

            <div className="flex items-baseline gap-4 relative z-10 group-hover:translate-x-4 transition-transform duration-medium">
              <span className="font-display text-2xl md:text-3xl uppercase tracking-tight group-hover:text-background transition-colors duration-medium">
                Strategy&
              </span>
              <span className="font-mono text-sm text-muted-foreground group-hover:text-background/60 transition-colors duration-medium">
                2024
              </span>
            </div>
            <div className="flex items-center gap-6 relative z-10">
              <span className="text-sm uppercase tracking-wider text-muted-foreground group-hover:text-background/60 transition-colors duration-medium hidden sm:block">
                Future Fabric · fine-tuned generation
              </span>
              <div className="w-8 h-8 rounded-full bg-muted group-hover:bg-background/20 flex items-center justify-center transition-all duration-medium group-hover:translate-x-0 translate-x-2 opacity-0 group-hover:opacity-100">
                <ArrowRight className="w-4 h-4 group-hover:text-background transition-colors duration-medium" />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Dynamic non-featured case studies */}
        {otherCaseStudies.map((project, index) =>
        <motion.div
          key={project.id}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{
            duration: 0.5,
            delay: index * 0.1,
            ease: easeStandard
          }}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}>
          
            <Link
            to={`/work/${project.slug}`}
            className="group flex items-center justify-between px-6 md:px-12 py-3 md:py-4 border-b border-border relative overflow-hidden min-h-[44px]">
            
              {/* Background slide effect */}
              <div className="absolute inset-0 bg-foreground translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-slow ease-out" />

              {/* Accent bar */}
              <div
              className="absolute left-0 top-0 bottom-0 w-1 scale-y-0 group-hover:scale-y-100 transition-transform duration-medium origin-top"
              style={{
                backgroundColor: project.accent_color || "hsl(var(--accent))"
              }} />
            

              <div className="flex items-baseline gap-4 relative z-10 group-hover:translate-x-4 transition-transform duration-medium">
                <span className="font-display text-2xl md:text-3xl uppercase tracking-tight group-hover:text-background transition-colors duration-medium">
                  {project.title}
                </span>
                <span className="font-mono text-sm text-muted-foreground group-hover:text-background/60 transition-colors duration-medium">
                  {project.year}
                </span>
              </div>

              <div className="flex items-center gap-6 relative z-10">
                <span className="text-sm uppercase tracking-wider text-muted-foreground group-hover:text-background/60 transition-colors duration-medium hidden sm:block">
                  {project.role || "Design"}
                </span>
                <div className="w-8 h-8 rounded-full bg-muted group-hover:bg-background/20 flex items-center justify-center transition-all duration-medium group-hover:translate-x-0 translate-x-2 opacity-0 group-hover:opacity-100">
                  <ArrowRight className="w-4 h-4 group-hover:text-background transition-colors duration-medium" />
                </div>
              </div>
            </Link>
          </motion.div>
        )}
      </div>

      {/* Empty state */}
      {otherCaseStudies.length === 0 && featured.length === 0 && !isLoading &&
      <div className="px-6 md:px-12 py-16 text-center">
          <p className="text-muted-foreground">No case studies published yet.</p>
        </div>
      }
    </section>);

};

export default CaseStudyList;